import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { CheckCircle2, Clock, Link as LinkIcon, Calendar, ArrowUpRight, Target, Users } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientReport = () => {
  const { accountId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(searchParams.get('month') || '');

  useEffect(() => {
    fetchReport();
  }, [accountId, selectedMonth]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = selectedMonth ? `?month=${selectedMonth}` : '';
      const response = await axios.get(`${API}/public/report/${accountId}${params}`);
      setReportData(response.data);
      
      // Set default month if not selected and we have data
      if (!selectedMonth && response.data.availableMonths?.length > 0) {
        const currentMonth = new Date().toISOString().slice(0, 7);
        const defaultMonth = response.data.availableMonths.includes(currentMonth) 
          ? currentMonth 
          : response.data.availableMonths[0];
        setSelectedMonth(defaultMonth);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    setSearchParams({ month });
  };

  const formatMonthDisplay = (monthStr) => {
    if (!monthStr) return '';
    try {
      const date = parseISO(`${monthStr}-01`);
      return format(date, 'MMMM yyyy');
    } catch {
      return monthStr;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(parseISO(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#b0cb1f]" />
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <p className="text-slate-600">Report not found</p>
      </div>
    );
  }

  const { account, completedTasks, ongoingTasks, stats, availableMonths } = reportData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <img 
              src="/logo.png" 
              alt="Content Vista" 
              className="h-10 w-auto"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">Report Period:</span>
            <Select value={selectedMonth} onValueChange={handleMonthChange}>
              <SelectTrigger className="w-48 bg-white" data-testid="month-selector">
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {availableMonths?.map((month) => (
                  <SelectItem key={month} value={month}>
                    {formatMonthDisplay(month)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <p className="text-[#b0cb1f] font-semibold text-sm uppercase tracking-wider mb-2">
                Monthly Progress Report
              </p>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{account.name}</h1>
              <p className="text-slate-300 text-lg">
                {formatMonthDisplay(selectedMonth)}
              </p>
            </div>
            <div 
              className="h-20 w-20 rounded-2xl flex items-center justify-center text-3xl font-bold"
              style={{ backgroundColor: account.color || '#b0cb1f' }}
            >
              {account.name?.charAt(0)}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
            <div className="bg-white/10 backdrop-blur rounded-xl p-6 border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <Target className="h-5 w-5 text-[#b0cb1f]" />
                <span className="text-slate-300 text-sm">Total Deliverables</span>
              </div>
              <p className="text-4xl font-bold">{stats.totalTasks}</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-6 border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                <span className="text-slate-300 text-sm">Completed</span>
              </div>
              <p className="text-4xl font-bold text-emerald-400">{stats.completedCount}</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-6 border border-white/10">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="h-5 w-5 text-amber-400" />
                <span className="text-slate-300 text-sm">In Progress</span>
              </div>
              <p className="text-4xl font-bold text-amber-400">{stats.ongoingCount}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Completed Work Section */}
        {completedTasks.length > 0 && (
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Completed Work</h2>
                <p className="text-slate-500 text-sm">Deliverables successfully completed this period</p>
              </div>
            </div>

            <div className="space-y-6">
              {completedTasks.map((task, index) => (
                <Card 
                  key={task.id} 
                  className="bg-white border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
                  data-testid={`completed-task-${task.id}`}
                >
                  <div className="border-l-4 border-emerald-500">
                    <div className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">
                              Completed
                            </span>
                            {task.week && (
                              <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                                {task.week}
                              </span>
                            )}
                            {task.taskType && (
                              <span className="px-2.5 py-1 bg-[#b0cb1f]/10 text-[#8da619] text-xs font-medium rounded-full">
                                {task.taskType}
                              </span>
                            )}
                          </div>
                          
                          <h3 className="text-xl font-semibold text-slate-900 mb-2">{task.title}</h3>
                          
                          {task.deliverable && (
                            <p className="text-slate-600 mb-1">
                              <span className="font-medium">Deliverable:</span> {task.deliverable}
                            </p>
                          )}

                          {task.instructions && (
                            <p className="text-slate-600 mb-4">
                              {task.instructions}
                            </p>
                          )}

                          {task.outcome && (
                            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 mb-4 border border-emerald-100">
                              <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-1">Outcome & Results</p>
                              <p className="text-slate-700">{task.outcome}</p>
                            </div>
                          )}

                          {task.links && task.links.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {task.links.map((link, idx) => (
                                <a
                                  key={idx}
                                  href={link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-[#b0cb1f]/20 text-slate-700 hover:text-[#8da619] rounded-lg text-sm font-medium transition-colors"
                                >
                                  <LinkIcon className="h-3.5 w-3.5" />
                                  View Resource
                                  <ArrowUpRight className="h-3 w-3" />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right shrink-0">
                          {task.dueDate && (
                            <div className="flex items-center gap-1.5 text-slate-500 text-sm">
                              <Calendar className="h-4 w-4" />
                              {formatDate(task.dueDate)}
                            </div>
                          )}
                          {task.responsibleNames?.length > 0 && (
                            <div className="flex items-center gap-1.5 text-slate-500 text-sm mt-2">
                              <Users className="h-4 w-4" />
                              {task.responsibleNames.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* In Progress Section */}
        {ongoingTasks.length > 0 && (
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">In Progress</h2>
                <p className="text-slate-500 text-sm">Work currently being executed</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {ongoingTasks.map((task) => (
                <Card 
                  key={task.id} 
                  className="bg-white border border-slate-200 p-5 hover:shadow-md transition-shadow"
                  data-testid={`ongoing-task-${task.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
                      <Clock className="h-4 w-4 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                          {task.status || 'In Progress'}
                        </span>
                        {task.week && (
                          <span className="text-xs text-slate-500">{task.week}</span>
                        )}
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-1">{task.title}</h3>
                      {task.deliverable && (
                        <p className="text-sm text-slate-600 truncate">{task.deliverable}</p>
                      )}
                      {task.dueDate && (
                        <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Due: {formatDate(task.dueDate)}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Empty State */}
        {completedTasks.length === 0 && ongoingTasks.length === 0 && (
          <div className="text-center py-20">
            <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Target className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No tasks for this period</h3>
            <p className="text-slate-500">Select a different month to view deliverables</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <img 
                src="/logo.png" 
                alt="Content Vista" 
                className="h-8 w-auto mb-3 brightness-0 invert"
              />
              <p className="text-slate-400 text-sm">
                Delivering exceptional content solutions
              </p>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-slate-400 text-sm">
                Report generated for {account.name}
              </p>
              <p className="text-slate-500 text-xs mt-1">
                {format(new Date(), 'MMMM dd, yyyy')}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClientReport;
